/**
 * Slice 31 — Live identify: point, tap, Gemini names it, strip is the review.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { useCreateInventoryItem, useInventoryItems } from "@/hooks/useInventory";
import { useCreateInventoryRoom, useInventoryRooms } from "@/hooks/useInventoryRooms";
import { useCreateUnitItem, useUnitItems } from "@/hooks/useUnitItems";
import { useAppliances, useCreateAppliance } from "@/hooks/useAppliances";
import { normalizeRoomName } from "@/lib/roomUtils";
import { useUpdateUnitItem } from "@/hooks/useUnitItems";
import { usePropertyRooms } from "@/hooks/usePropertyRooms";
import { useCreatePhotoGroup, usePhotoGroups, useUpdatePhotoGroup } from "@/hooks/usePhotoGroups";
import { defaultGroupPrompt, sameRoom } from "@/lib/photoGroups";
import {
  ConfirmGrouped,
  ExistingItemsSheet,
  ModeToggle,
  MoveSheet,
  RoomSummaryScreen,
  SpotNameSheet,
  SpotOpenBar,
  SpotPickSheet,
  SpotReview,
  SpotSavedScreen,
  SpotShoot,
  SpotsDoneScreen,
  type ConfirmRow,
  type ExistingPick,
  type Spot,
} from "@/components/owner/WalkthroughSpots";
import { ReviewSheet } from "@/components/owner/WalkthroughReview";
import { useInventoryLeaveOutRules } from "@/hooks/useInventoryLeaveOutRules";
import { supabase } from "@/integrations/supabase/client";
import { useCreatePart } from "@/hooks/usePropertyParts";
import { emptyPartDraft, partSummary, type PartDraft } from "@/lib/parts";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryKeys";
import { syncGuestGuideKnowledge } from "@/lib/guestGuideHospitable";
import {
  buildLeavePlan,
  extrasHeadlineQty,
  extrasPlaceEntries,
  extrasPlaceLabels,
  guestNoteText,
  joinPlaces,
  type ExtrasMode,
  type ExtrasRow,
} from "@/lib/walkthroughReview";
import { captureBestJpeg, blobToBase64, isDesktopCaptureViewport, roomsCaptureUrl, STICKER_STILL_MAX_EDGE, STICKER_STILL_QUALITY } from "@/lib/captureStill";
import {
  analyzeWalkthroughItem,
  uploadWalkthroughStill,
  WalkthroughAnalyzeError,
  type WalkthroughKind,
} from "@/lib/analyzeWalkthrough";
import { LEVEL_NOW_OPTIONS, formatLevelNow } from "@/lib/inventoryCatalog";
import { inventoryNowAndLow, matchInventoryCatalog, parseSpokenLevel } from "@/lib/walkthroughThresholds";

type Stage = "pick" | "start" | "live" | "confirm" | "saved" | "spotsDone" | "summary";

type TileStatus = "reading" | "named" | "failed";

type CaptureTile = {
  id: string;
  localUrl: string;
  photoUrl: string | null;
  stickerLocalUrl: string | null;
  stickerPhotoUrl: string | null;
  name: string;
  kind: WalkthroughKind | "part";
  /** Part only: what is being recorded (type, spec, where, spare setting). */
  part: PartDraft;
  /** Inventory only. units = how many. fullness = how full one container is. */
  track: "units" | "fullness";
  /** null = she opened Level and has not tapped a chip yet. Done saves that as Full. */
  level: number | null;
  /** She changed Count, Level, or the number. A retake must not flip it. */
  measureLocked: boolean;
  count: number;
  model: string | null;
  serial: string | null;
  spoken_number: number | null;
  spoken_level: string | null;
  spoken_text: string | null;
  status: TileStatus;
  failReason?: string;
  /** Slice 59 — the spot (photo group) she was in when she tapped this, null = whole room. */
  spotId: string | null;
  /** Supplies: restock when this many are left (count) or at this level. */
  restock: number;
  buyAt: number;
  /** Extras for guests: skip, same every stay, or by party size; and where they go. */
  extrasMode: ExtrasMode;
  extrasQty: number;
  extrasRows: ExtrasRow[];
  extrasPlaces: string[];
  extrasCustom: string;
  /** Tell guests where it is (Hospitable Knowledge Hub). */
  guestOn: boolean;
  applPlace: string | null;
};



const HINT_KEY = "stravo-capture-hint-seen";
const VIEWPORT = "width=device-width, initial-scale=1.0, viewport-fit=cover";
const VIEWPORT_LOCKED = "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover";

function setViewport(content: string) {
  document.querySelector('meta[name="viewport"]')?.setAttribute("content", content);
}

function newId() {
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function kindMeta(tile: CaptureTile): string {
  if (tile.kind === "part") return partSummary({ part_type: tile.part.type, spec: tile.part.type === "other" ? { rows: tile.part.rows } : tile.part.spec, quantity: tile.count });
  if (tile.kind === "appliance") {
    const bits = [tile.model, tile.serial].filter(Boolean);
    return bits.length ? bits.join(" · ") : "Appliance";
  }
  if (tile.kind === "inventory" && tile.track === "fullness") {
    return formatLevelNow(tile.level ?? 1);
  }
  return String(tile.count);
}

function levelSpoken(value: number): string {
  if (value >= 0.9) return "full";
  if (value >= 0.7) return "3/4";
  if (value >= 0.4) return "1/2";
  if (value >= 0.1) return "1/4";
  return "empty";
}

function levelHint(name: string): string {
  const n = name.toLowerCase();
  if (/\b(bag|bags|roll)\b/.test(n)) return "One roll.";
  if (/\bcleaner\b|\bwindex\b|\blys?ol\b|\bspray\b/.test(n)) return "One spray bottle.";
  return "One bottle.";
}

export function CapturePhoneHint({
  propertyId,
  onClose,
}: {
  propertyId: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    const url = roomsCaptureUrl(propertyId);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast({ title: "Link copied — open it on your phone" });
    } catch {
      toast({ title: url, description: "Copy this on your phone" });
    }
  };
  return (
    <div className="flex h-full flex-col items-center justify-center bg-[#f4f7f9] px-6">
      <div className="w-full max-w-[620px] rounded-[18px] border border-[#e6edf1] bg-white px-8 py-10 text-center">
        <div className="mx-auto flex h-[110px] w-[66px] items-end justify-center rounded-[12px] border-[2.5px] border-[#24a89a] pb-2">
          <div className="h-[3px] w-[22px] rounded-[2px] bg-[#24a89a]" />
        </div>
        <h2 className="mt-4 text-[26px] font-bold tracking-[-0.5px] text-[#0f1720]">Use your phone to onboard</h2>
        <p className="mx-auto mt-2 max-w-[420px] text-[15px] font-medium leading-snug text-[#64748b]">
          Capture happens in the room, standing in front of the thing. Open this on the phone you’re standing with.
          Point at a thing, tap once, and the name writes itself.
        </p>
        <button
          type="button"
          onClick={() => void copyLink()}
          className="mt-5 h-[52px] rounded-[14px] bg-[#24a89a] px-[22px] text-[16px] font-bold text-white"
        >
          {copied ? "Copied" : "Text me the link"}
        </button>
        <button type="button" className="mt-3 h-11 text-[15px] font-medium text-[#64748b]" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

export function WalkthroughFlow(props: {
  open: boolean;
  propertyId: string;
  roomName?: string | null;
  roomNames: string[];
  onClose: () => void;
  onImported?: (room: string, inventoryImported?: number) => void;
}) {
  if (!props.open) return null;
  return createPortal(<WalkthroughInner {...props} />, document.body);
}

function WalkthroughInner({
  propertyId,
  roomName,
  roomNames,
  onClose,
  onImported,
}: {
  propertyId: string;
  roomName?: string | null;
  roomNames: string[];
  onClose: () => void;
  onImported?: (room: string, inventoryImported?: number) => void;
}) {
  const startRoom = (roomName || "").trim();
  const [stage, setStage] = useState<Stage>(startRoom ? "live" : "pick");
  const [spotPicker, setSpotPicker] = useState(false);
  // Items already captured in this room that the owner ticked into a spot (no retaking their photos).
  const [spotExisting, setSpotExisting] = useState<Record<string, string[]>>({});
  const [existingFor, setExistingFor] = useState<{ spotId: string; name: string; groupId: string | null } | null>(null);
  // "Add spots" walk: photograph spots and move saved items into them, no new item photos.
  const [spotsMode, setSpotsMode] = useState(false);
  const [savedSpotId, setSavedSpotId] = useState<string | null>(null);
  /** Saved spot (photo group) a spot in this walk replaces, by walk spot id. */
  const [spotSavedGroup, setSpotSavedGroup] = useState<Record<string, string>>({});
  const [spotsDoneRows, setSpotsDoneRows] = useState<{ key: string; name: string; items: number; photoUrl: string | null; note: string }[]>([]);
  const queryClient = useQueryClient();
  const [retakeId, setRetakeId] = useState<string | null>(null);
  const [plateEditing, setPlateEditing] = useState(false);
  const [savedCounts, setSavedCounts] = useState<{ items: number; spots: number; supplies: number } | null>(null);
  const [currentRoom, setCurrentRoom] = useState(startRoom);
  const [tiles, setTiles] = useState<CaptureTile[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [aimingId, setAimingId] = useState<string | null>(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [desktopHint, setDesktopHint] = useState(() => isDesktopCaptureViewport());
  const [showHint, setShowHint] = useState(() => {
    try {
      return sessionStorage.getItem(HINT_KEY) !== "1";
    } catch {
      return true;
    }
  });
  const [busyShutter, setBusyShutter] = useState(false);

  // Slice 59 — spots (photo groups). Where you are is a mode: set it once, then keep tapping.
  const [spots, setSpots] = useState<Spot[]>([]);
  const [currentSpotId, setCurrentSpotId] = useState<string | null>(null);
  const [spotDraft, setSpotDraft] = useState<{ name: string; replaceId?: string } | null>(null);
  const [spotShot, setSpotShot] = useState<{ localUrl: string; blob: Blob } | null>(null);
  const [spotHint, setSpotHint] = useState<string | null>(null);
  const [moveRow, setMoveRow] = useState<ConfirmRow | null>(null);
  const spotBlobsRef = useRef<Map<string, Blob>>(new Map());

  const videoLiveRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stripRef = useRef<HTMLDivElement | null>(null);
  const tilesRef = useRef<CaptureTile[]>([]);
  const blobByTileRef = useRef<Map<string, Blob>>(new Map());
  const stickerBlobByTileRef = useRef<Map<string, Blob>>(new Map());
  const identifyInFlightRef = useRef(0);
  const identifyWaitersRef = useRef<Array<() => void>>([]);
  tilesRef.current = tiles;

  const MAX_IDENTIFY_IN_FLIGHT = 5;

  const withIdentifySlot = async <T,>(fn: () => Promise<T>): Promise<T> => {
    if (identifyInFlightRef.current >= MAX_IDENTIFY_IN_FLIGHT) {
      await new Promise<void>((resolve) => identifyWaitersRef.current.push(resolve));
    }
    identifyInFlightRef.current += 1;
    try {
      return await fn();
    } finally {
      identifyInFlightRef.current -= 1;
      identifyWaitersRef.current.shift()?.();
    }
  };

  const leaveOut = useInventoryLeaveOutRules(propertyId);
  const createInv = useCreateInventoryItem();
  const createPart = useCreatePart(propertyId);
  const updateUnit = useUpdateUnitItem();
  const createGroup = useCreatePhotoGroup();
  const updateGroup = useUpdatePhotoGroup();
  const { data: propertyRooms = [] } = usePropertyRooms(propertyId);
  const { data: existingGroups = [] } = usePhotoGroups(propertyId);
  const createUnit = useCreateUnitItem();
  const createRoom = useCreateInventoryRoom();
  const createAppliance = useCreateAppliance(propertyId);
  const { data: existingInv = [] } = useInventoryItems(propertyId);
  const { data: existingInvRooms = [] } = useInventoryRooms(propertyId);
  const { data: existingUnit = [] } = useUnitItems(propertyId);
  const { data: existingAppliances = [] } = useAppliances(propertyId);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    return () => {
      stopStream();
      tiles.forEach((t) => {
        if (t.localUrl.startsWith("blob:")) URL.revokeObjectURL(t.localUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCamera = useCallback(async () => {
    if (isDesktopCaptureViewport()) {
      setDesktopHint(true);
      return;
    }
    setDesktopHint(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      const track = stream.getVideoTracks()[0];
      if (track) {
        try {
          await track.applyConstraints({
            advanced: [{ focusMode: "continuous" } as unknown as MediaTrackConstraintSet],
          });
        } catch {
          /* phones without focusMode */
        }
      }
      if (videoLiveRef.current) {
        videoLiveRef.current.srcObject = stream;
        await videoLiveRef.current.play().catch(() => undefined);
      }
    } catch {
      setDesktopHint(true);
    }
  }, []);

  useEffect(() => {
    if (stage === "live" && currentRoom && !desktopHint) void startCamera();
  }, [stage, currentRoom, desktopHint, startCamera]);

  useEffect(() => {
    if (!showHint) return;
    try {
      sessionStorage.setItem(HINT_KEY, "1");
    } catch {
      /* ignore */
    }
  }, [showHint]);

  useEffect(() => {
    if (expandedId) return;
    setViewport(VIEWPORT);
  }, [expandedId]);

  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth;
  }, [tiles.length]);

  const patchTile = (id: string, patch: Partial<CaptureTile>) => {
    setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  const alreadyInRoom = useCallback(
    (kind: WalkthroughKind | "part", name: string, room: string) => {
      if (kind === "part") return false;
      const n = normalizeRoomName(name);
      const r = normalizeRoomName(room);
      if (kind === "unit") {
        return existingUnit.some(
          (it) => normalizeRoomName(it.name) === n && normalizeRoomName(it.room || "") === r,
        );
      }
      if (kind === "appliance") {
        return existingAppliances.some(
          (it) => normalizeRoomName(it.name) === n && normalizeRoomName(it.location || "") === r,
        );
      }
      const invRoom = existingInvRooms.find((ir) => normalizeRoomName(ir.name) === r);
      if (!invRoom) return false;
      return existingInv.some((it) => {
        const item = it as { item_name?: string; room_id?: string | null };
        return normalizeRoomName(item.item_name || "") === n && item.room_id === invRoom.id;
      });
    },
    [existingAppliances, existingInv, existingInvRooms, existingUnit],
  );

  const runIdentify = async (opts: {
    tileId: string;
    blob: Blob;
    replace?: boolean;
    plateFor?: string | null;
  }) => {
    const aiming = opts.plateFor;
    if (aiming) {
      stickerBlobByTileRef.current.set(aiming, opts.blob);
      void uploadWalkthroughStill(opts.blob, propertyId)
        .then((photoUrl) => patchTile(aiming, { stickerPhotoUrl: photoUrl }))
        .catch(() => undefined);
    } else {
      blobByTileRef.current.set(opts.tileId, opts.blob);
      void uploadWalkthroughStill(opts.blob, propertyId)
        .then((photoUrl) => patchTile(opts.tileId, { photoUrl }))
        .catch(() => undefined);
    }

    await withIdentifySlot(async () => {
      try {
        const imageBase64 = await blobToBase64(opts.blob);
        const attachName = aiming ? tilesRef.current.find((t) => t.id === aiming)?.name : null;
        let item: Awaited<ReturnType<typeof analyzeWalkthroughItem>> | null = null;
        let lastErr: unknown = null;
        try {
          item = await analyzeWalkthroughItem({
            propertyId,
            imageBase64,
            room: currentRoom,
            mode: aiming ? "model_plate" : "walkthrough_item",
            attachHint: attachName,
          });
        } catch (err) {
          lastErr = err;
          await new Promise((r) => setTimeout(r, 200));
          try {
            item = await analyzeWalkthroughItem({
              propertyId,
              imageBase64,
              room: currentRoom,
              mode: aiming ? "model_plate" : "walkthrough_item",
              attachHint: attachName,
            });
            lastErr = null;
          } catch (err2) {
            lastErr = err2;
          }
        }
        if (!item) {
          throw lastErr instanceof Error ? lastErr : new Error("Couldn’t name");
        }
        const named = item;
        const prev = tilesRef.current.find((t) => t.id === (aiming || opts.tileId));
        const keepMeasure = Boolean(opts.plateFor || opts.replace || prev?.measureLocked);
        const catalog = matchInventoryCatalog(named.name);
        const guessedLevel = named.kind === "inventory" && catalog.track === "fullness";
        if (aiming) {
          patchTile(aiming, {
            model: named.model || tilesRef.current.find((t) => t.id === aiming)?.model || null,
            serial: named.serial || tilesRef.current.find((t) => t.id === aiming)?.serial || null,
            kind: tilesRef.current.find((t) => t.id === aiming)?.kind === "part" ? "part" : "appliance",
            status: "named",
          });
          setAimingId(null);
          setExpandedId(aiming);
          return;
        }
        patchTile(opts.tileId, {
          name: named.name,
          kind: keepMeasure && prev ? prev.kind : named.kind,
          count: keepMeasure && prev ? prev.count : named.count ?? 1,
          track: keepMeasure && prev ? prev.track : guessedLevel ? "fullness" : "units",
          level:
            keepMeasure && prev
              ? prev.level
              : guessedLevel
                ? parseSpokenLevel(named.spoken_level) ?? 1
                : null,
          measureLocked: keepMeasure && prev ? prev.measureLocked : false,
          model: named.model,
          serial: named.serial ?? null,
          spoken_number: null,
          spoken_level: null,
          spoken_text: null,
          status: "named",
        });
      } catch (err) {
        const msg = err instanceof WalkthroughAnalyzeError ? err.message : "Couldn’t name";
        if (opts.plateFor) {
          toast({ title: "Couldn’t read the sticker", description: msg });
          return;
        }
        patchTile(opts.tileId, { status: "failed", failReason: msg });
      }
    });
  };

  const identifyBlob = (opts: { tileId: string; blob: Blob; replace?: boolean; plateFor?: string | null }) => {
    void runIdentify(opts);
  };

  const ensurePhotoUrl = async (tile: CaptureTile): Promise<string | null> => {
    if (tile.photoUrl) return tile.photoUrl;
    const blob = blobByTileRef.current.get(tile.id);
    if (!blob) return null;
    try {
      const photoUrl = await uploadWalkthroughStill(blob, propertyId);
      patchTile(tile.id, { photoUrl });
      return photoUrl;
    } catch {
      return null;
    }
  };

  const ensureStickerPhotoUrl = async (tile: CaptureTile): Promise<string | null> => {
    if (tile.stickerPhotoUrl) return tile.stickerPhotoUrl;
    const blob = stickerBlobByTileRef.current.get(tile.id);
    if (!blob) return null;
    try {
      const stickerPhotoUrl = await uploadWalkthroughStill(blob, propertyId);
      patchTile(tile.id, { stickerPhotoUrl });
      return stickerPhotoUrl;
    } catch {
      return null;
    }
  };

  const shutter = async (replaceId?: string) => {
    if (busyShutter) return;
    const video = videoLiveRef.current;
    if (!video) return;
    setBusyShutter(true);
    const blob = await captureBestJpeg(
      video,
      streamRef.current,
      aimingId && !replaceId
        ? { maxEdge: STICKER_STILL_MAX_EDGE, quality: STICKER_STILL_QUALITY }
        : undefined,
    );
    if (!blob) {
      setBusyShutter(false);
      return;
    }
    setShowHint(false);
    const localUrl = URL.createObjectURL(blob);
    const id = replaceId || newId();
    try {
      if (replaceId) {
        setTiles((prev) =>
          prev.map((t) =>
            t.id === replaceId
              ? { ...t, localUrl, photoUrl: null, status: "reading", name: "", failReason: undefined }
              : t,
          ),
        );
        setExpandedId(replaceId);
        void identifyBlob({ tileId: id, blob, replace: true, plateFor: null });
      } else if (aimingId) {
        if (tilesRef.current.find((t) => t.id === aimingId)?.stickerLocalUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(tilesRef.current.find((t) => t.id === aimingId)!.stickerLocalUrl!);
        }
        patchTile(aimingId, { stickerLocalUrl: localUrl });
        void identifyBlob({ tileId: aimingId, blob, plateFor: aimingId });
      } else {
        const tile: CaptureTile = {
          id,
          localUrl,
          photoUrl: null,
          stickerLocalUrl: null,
          stickerPhotoUrl: null,
          name: "",
          kind: "unit",
          part: emptyPartDraft(),
          track: "units",
          level: null,
          measureLocked: false,
          count: 1,
          model: null,
          serial: null,
          spoken_number: null,
          spoken_level: null,
          spoken_text: null,
          status: "reading",
          spotId: currentSpotId,
          restock: 1,
          buyAt: 0.25,
          extrasMode: "per_guests",
          extrasQty: 1,
          extrasRows: [{ id: "r1", guests: 2, qty: 2 }],
          extrasPlaces: ["spot"],
          extrasCustom: "",
          guestOn: true,
          applPlace: null,
        };
        setSpotHint(null);
        setTiles((prev) => [...prev, tile]);
        setExpandedId(id);
        void identifyBlob({ tileId: id, blob });
      }
    } finally {
      setBusyShutter(false);
    }
  };

  const startRetake = (id: string) => {
    setRetakeId(id);
    setExpandedId(null);
    setSpotHint("Retaking. Tap the shutter.");
  };

  const namedTiles = tiles.filter((t) => t.status === "named");

  // ---------- Slice 59 spots ----------
  const currentSpot = spots.find((sp) => sp.id === currentSpotId) ?? null;
  const spotTiles = (id: string | null) => tiles.filter((t) => t.spotId === id);
  const namedCount = (id: string | null) => spotTiles(id).filter((t) => t.status === "named").length;
  const toRetake = (id: string | null) => spotTiles(id).filter((t) => t.status === "failed").length;

  const uploadSpotPhoto = async (spot: Spot): Promise<string | null> => {
    if (spot.photoUrl) return spot.photoUrl;
    const blob = spotBlobsRef.current.get(spot.id);
    if (!blob) return null;
    try {
      const url = await uploadWalkthroughStill(blob, propertyId);
      setSpots((prev) => prev.map((x) => (x.id === spot.id ? { ...x, photoUrl: url } : x)));
      return url;
    } catch {
      return null;
    }
  };

  const shootSpot = async () => {
    if (busyShutter) return;
    const video = videoLiveRef.current;
    if (!video) return;
    setBusyShutter(true);
    try {
      const blob = await captureBestJpeg(video, streamRef.current);
      if (!blob) return;
      setSpotShot({ blob, localUrl: URL.createObjectURL(blob) });
    } finally {
      setBusyShutter(false);
    }
  };

  /** Every item already saved in this room, with the spot it is in (if any). */
  const roomSavedItems = (): (ExistingPick & { groupId: string | null })[] => {
    const r = normalizeRoomName(currentRoom);
    const invRoomIds = new Set(existingInvRooms.filter((ir) => normalizeRoomName(ir.name) === r).map((ir) => ir.id));
    const out: (ExistingPick & { groupId: string | null })[] = [];
    for (const it of existingUnit) {
      if (normalizeRoomName(it.room || "") !== r) continue;
      out.push({ key: `unit:${it.id}`, name: it.name, photoUrl: it.photo_url ?? null, kind: "unit", groupId: it.photo_group_id ?? null });
    }
    for (const it of existingInv as { id: string; item_name?: string | null; photo_url?: string | null; room_id?: string | null; photo_group_id?: string | null }[]) {
      if (!it.room_id || !invRoomIds.has(it.room_id)) continue;
      out.push({ key: `supply:${it.id}`, name: it.item_name || "Supply", photoUrl: it.photo_url ?? null, kind: "supply", groupId: it.photo_group_id ?? null });
    }
    for (const it of existingAppliances as { id: string; name?: string | null; location?: string | null; photo_urls?: string[] | null; photo_group_id?: string | null }[]) {
      if (normalizeRoomName(it.location || "") !== r) continue;
      out.push({ key: `appliance:${it.id}`, name: it.name || "Appliance", photoUrl: it.photo_urls?.[0] ?? null, kind: "appliance", groupId: it.photo_group_id ?? null });
    }
    return out;
  };
  const groupNameById = (id: string | null) => existingGroups.find((g) => g.id === id)?.name ?? null;

  /**
   * Items to offer for a spot: not in any spot yet, plus (dimmed) items in another spot, plus the spot's own
   * items when it is a saved spot being retaken. Items already ticked into another spot of this walk are hidden.
   */
  const existingCandidates = (forGroupId: string | null = null, forSpotId: string | null = null): ExistingPick[] => {
    const claimed = new Set(
      Object.entries(spotExisting)
        .filter(([sid]) => sid !== forSpotId)
        .flatMap(([, keys]) => keys),
    );
    return roomSavedItems()
      .filter((it) => !claimed.has(it.key))
      .map((it) => ({
        key: it.key,
        name: it.name,
        photoUrl: it.photoUrl,
        kind: it.kind,
        inSpot: it.groupId && it.groupId !== forGroupId ? groupNameById(it.groupId) : null,
      }));
  };

  const useSpotPhoto = (nameOverride?: string) => {
    if (!spotDraft || !spotShot) return;
    const { replaceId } = spotDraft;
    const name = nameOverride || spotDraft.name;
    const { blob, localUrl } = spotShot;
    const id = replaceId ?? newId();
    spotBlobsRef.current.set(id, blob);
    if (replaceId) {
      setSpots((prev) => prev.map((x) => (x.id === id ? { ...x, localUrl, photoUrl: null } : x)));
    } else {
      const roomRow = propertyRooms.find((r) => sameRoom(r.name, currentRoom));
      const savedGroup = roomRow
        ? existingGroups.find((g) => g.room_id === roomRow.id && normalizeRoomName(g.name) === normalizeRoomName(name))
        : undefined;
      if (savedGroup) setSpotSavedGroup((prev) => ({ ...prev, [id]: savedGroup.id }));
      setSpots((prev) => [...prev, { id, name: savedGroup?.name ?? name, localUrl, photoUrl: null }]);
      setCurrentSpotId(spotsMode ? null : id);
      setSpotHint(`${name} is set. Tap what’s inside.`);
      const cands = existingCandidates(savedGroup?.id ?? null, id);
      if (cands.length > 0) setExistingFor({ spotId: id, name: savedGroup?.name ?? name, groupId: savedGroup?.id ?? null });
      else if (spotsMode) setSavedSpotId(id);
    }
    void uploadWalkthroughStill(blob, propertyId)
      .then((url) => setSpots((prev) => prev.map((x) => (x.id === id ? { ...x, photoUrl: url } : x))))
      .catch(() => undefined);
    setSpotShot(null);
    setSpotDraft(null);
  };

  const removeSpot = (id: string) => {
    setSpots((prev) => prev.filter((x) => x.id !== id));
    setSpotExisting((prev) => {
      const { [id]: _drop, ...rest } = prev;
      void _drop;
      return rest;
    });
    setTiles((prev) => prev.map((t) => (t.spotId === id ? { ...t, spotId: null } : t)));
    if (currentSpotId === id) setCurrentSpotId(null);
  };

  const requestClose = () => {
    if (tiles.length === 0) {
      stopStream();
      onClose();
      return;
    }
    setLeaveOpen(true);
  };

  const discard = () => {
    tiles.forEach((t) => {
      if (t.localUrl.startsWith("blob:")) URL.revokeObjectURL(t.localUrl);
      if (t.stickerLocalUrl?.startsWith("blob:")) URL.revokeObjectURL(t.stickerLocalUrl);
    });
    blobByTileRef.current.clear();
    stickerBlobByTileRef.current.clear();
    setTiles([]);
    setLeaveOpen(false);
    stopStream();
    onClose();
  };

  const goConfirm = () => {
    const keep = tiles.filter((t) => t.status === "named");
    setTiles(keep);
    if (!keep.length && spots.length === 0) {
      toast({ title: "Nothing to import", description: "Retake a tile that couldn’t be read." });
      return;
    }
    setExpandedId(null);
    setStage("confirm");
  };

  const handleImport = async () => {
    if ((!namedTiles.length && spots.length === 0) || importing) return;
    setImporting(true);
    try {
      const room = await createRoom.mutateAsync({ propertyId, name: currentRoom });

      // Slice 59: save each spot as a photo group (with its reference photo) before the items that use it.
      const groupIdBySpot = new Map<string, string>();
      const roomRow = propertyRooms.find((r) => sameRoom(r.name, currentRoom));
      if (spots.length > 0) {
        if (!roomRow) {
          toast({ title: "Spots weren’t saved", description: "Add this room in the Rooms tab first, then walk it again." });
        } else {
          try {
            const inRoom = existingGroups.filter((g) => g.room_id === roomRow.id);
            let nextOrder = inRoom.length ? Math.max(...inRoom.map((g) => g.sort_order)) + 1 : 0;
            for (const spot of spots) {
              const url = await uploadSpotPhoto(spot);
              const found = inRoom.find((g) => normalizeRoomName(g.name) === normalizeRoomName(spot.name));
              if (found) {
                if (url) await updateGroup.mutateAsync({ id: found.id, propertyId, referencePhotoUrl: url });
                groupIdBySpot.set(spot.id, found.id);
              } else {
                const made = await createGroup.mutateAsync({
                  propertyId,
                  roomId: roomRow.id,
                  name: spot.name,
                  prompt: defaultGroupPrompt(spot.name),
                  sortOrder: nextOrder++,
                  referencePhotoUrl: url,
                });
                groupIdBySpot.set(spot.id, made.id);
              }
            }
          } catch (err) {
            toast({
              title: "Spots weren’t saved",
              description: (err as { message?: string } | null)?.message || undefined,
              variant: "destructive",
            });
          }
        }
      }

      const planBatch: Parameters<typeof leaveOut.upsertPlans>[0] = [];
      const spotNameOf = (id: string | null) => spots.find((x) => x.id === id)?.name ?? null;
      const othersOf = (id: string | null) => spots.filter((x) => x.id !== id).map((x) => ({ id: x.id, name: x.name }));
      /** Guest note + Hospitable Knowledge Hub for a supply or appliance, same fields the Inventory tab edits. */
      const saveGuestNote = async (row: CaptureTile, table: "inventory_items" | "appliances", id: string) => {
        const note = guestNoteText(row, spotNameOf(row.spotId), othersOf(row.spotId), currentRoom);
        const payload = note
          ? {
              guest_visible: true,
              guest_set_qty: row.kind === "inventory" ? extrasHeadlineQty(row) : null,
              guest_place:
                row.kind === "inventory"
                  ? joinPlaces(extrasPlaceLabels(row, spotNameOf(row.spotId), othersOf(row.spotId), currentRoom))
                  : row.applPlace?.trim() || spotNameOf(row.spotId) || currentRoom,
              guest_copy: note,
              guest_guide_status: "yes",
            }
          : { guest_visible: false, guest_set_qty: null, guest_place: null, guest_copy: null, guest_guide_status: "no" };
        const { error } = await supabase.from(table).update(payload as never).eq("id", id);
        if (error) throw error;
        if (note) {
          await syncGuestGuideKnowledge({
            propertyId,
            source: table === "appliances" ? "appliance" : "inventory",
            itemId: id,
            content: note,
          });
        }
      };
      const setGroup = async (table: "inventory_items" | "appliances", id: string, groupId: string | null) => {
        if (!groupId) return;
        await supabase.from(table).update({ photo_group_id: groupId } as never).eq("id", id);
      };

      let inventoryImported = 0;
      for (const row of namedTiles) {
        const groupId = row.spotId ? groupIdBySpot.get(row.spotId) ?? null : null;
        if (row.kind === "part") {
          const d = row.part;
          const placePhoto = await ensurePhotoUrl(row);
          const labelPhoto = await ensureStickerPhotoUrl(row);
          const specOut: Record<string, unknown> =
            d.type === "other"
              ? { rows: d.rows.filter((r) => r.label.trim() || r.value.trim()).slice(0, 4) }
              : Object.fromEntries(Object.entries(d.spec).filter(([, v]) => v));
          await createPart.mutateAsync({
            part_type: d.type,
            name: row.name || "Part",
            room: currentRoom,
            place: d.place.trim() || spotNameOf(row.spotId) || null,
            spec: specOut,
            quantity: Math.max(1, row.count || 1),
            brand: d.brand.trim() || null,
            model: row.model?.trim() || null,
            store: d.store.trim() || null,
            photo_place_url: placePhoto,
            photo_label_url: labelPhoto,
            replace_every_months: d.replaceMonths,
            cleaner_can_replace: d.canReplace,
            keep_spares: Math.max(0, d.keepSpares),
          });
          continue;
        }
        if (alreadyInRoom(row.kind, row.name, currentRoom)) {
          // Already imported before: walking it again inside a spot still groups it.
          if (row.kind === "unit" && groupId) {
            const existing = existingUnit.find(
              (it) =>
                normalizeRoomName(it.name) === normalizeRoomName(row.name) &&
                normalizeRoomName(it.room || "") === normalizeRoomName(currentRoom),
            );
            if (existing) await updateUnit.mutateAsync({ id: existing.id, propertyId, photoGroupId: groupId });
          }
          continue;
        }
        const photoUrl = await ensurePhotoUrl(row);
        if (row.kind === "unit") {
          await createUnit.mutateAsync({
            propertyId,
            name: row.name,
            room: currentRoom,
            photoUrl,
            photoGroupId: groupId,
          });
        } else if (row.kind === "appliance") {
          const stickerUrl = await ensureStickerPhotoUrl(row);
          const photos = [photoUrl, stickerUrl].filter(Boolean) as string[];
          const made = await createAppliance.mutateAsync({
            name: row.name,
            location: currentRoom,
            model_number: row.model,
            serial_number: row.serial,
            photo_urls: photos,
          });
          await setGroup("appliances", made.id, groupId);
          await saveGuestNote(row, "appliances", made.id);
        } else {
          const track = row.track === "fullness" ? "fullness" : "units";
          const qty = inventoryNowAndLow({
            track,
            spokenNumber: Math.max(1, row.count || 1),
            spokenLevel: track === "fullness" ? levelSpoken(row.level ?? 1) : null,
          });
          const made = (await createInv.mutateAsync({
            propertyId,
            itemName: row.name,
            photoUrl,
            roomId: room.id,
            quantityType: qty.quantityType,
            currentCount: qty.currentCount,
            thresholdCount: track === "units" ? Math.max(0, row.restock) : qty.thresholdCount,
            currentValue: qty.currentValue,
            thresholdValue: track === "fullness" ? row.buyAt : qty.thresholdValue,
            inventoryScope: "guest_amenities",
          })) as { id: string };
          await setGroup("inventory_items", made.id, groupId);
          await saveGuestNote(row, "inventory_items", made.id);
          const plan = buildLeavePlan(
            row,
            extrasPlaceEntries(row, spotNameOf(row.spotId), row.spotId, othersOf(row.spotId), currentRoom).map((e) => ({
              label: e.label,
              groupId: e.spotId ? groupIdBySpot.get(e.spotId) ?? null : null,
            })),
          );
          if (plan) planBatch.push({ itemId: made.id, itemName: row.name, plan });
          inventoryImported += 1;
        }
      }
      // Items that were already saved and ticked into a spot: just move them, no new photos.
      let movedExisting = 0;
      for (const [spotId, keys] of Object.entries(spotExisting)) {
        const gid = groupIdBySpot.get(spotId);
        if (!gid) continue;
        // A saved spot that was retaken: items the owner unticked go back to "In the open".
        if (spotSavedGroup[spotId]) {
          for (const it of roomSavedItems()) {
            if (it.groupId === gid && !keys.includes(it.key)) {
              const [kind, id] = it.key.split(":");
              if (kind === "unit") await updateUnit.mutateAsync({ id, propertyId, photoGroupId: null });
              else await supabase.from(kind === "supply" ? "inventory_items" : "appliances").update({ photo_group_id: null } as never).eq("id", id);
            }
          }
        }
        for (const key of keys) {
          const [kind, id] = key.split(":");
          if (kind === "unit") await updateUnit.mutateAsync({ id, propertyId, photoGroupId: gid });
          else await setGroup(kind === "supply" ? "inventory_items" : "appliances", id, gid);
          movedExisting += 1;
        }
      }
      if (planBatch.length > 0) await leaveOut.upsertPlans(planBatch);
      toast({ title: "Imported" });
      setSavedCounts({
        items: namedTiles.length + movedExisting,
        spots: spots.length,
        supplies: namedTiles.filter((t) => t.kind === "inventory").length,
      });
      onImported?.(currentRoom, inventoryImported);
      stopStream();
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.byProperty(propertyId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.appliances(propertyId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.unitItems.byProperty(propertyId) });
      void queryClient.invalidateQueries({ queryKey: ["photo-groups", propertyId] });
      if (spotsMode) {
        const roomRowNow = propertyRooms.find((r) => sameRoom(r.name, currentRoom));
        const retakenIds = new Set(Object.values(spotSavedGroup));
        const rowsDone = spots.map((sp) => {
          const savedId = spotSavedGroup[sp.id];
          const savedG = savedId ? existingGroups.find((g) => g.id === savedId) : null;
          const picks = spotExisting[sp.id];
          const itemsNow = picks
            ? picks.length
            : savedId
              ? roomSavedItems().filter((i) => i.groupId === savedId).length
              : 0;
          return {
            key: sp.id,
            name: sp.name,
            items: itemsNow,
            photoUrl: sp.localUrl,
            note: savedG ? (savedG.reference_photo_url ? "Photo replaced" : "Photo added") : "New",
          };
        });
        const kept = roomRowNow
          ? existingGroups
              .filter((g) => g.room_id === roomRowNow.id && !retakenIds.has(g.id))
              .map((g) => ({
                key: g.id,
                name: g.name,
                items: roomSavedItems().filter((i) => i.groupId === g.id).length,
                photoUrl: g.reference_photo_url,
                note: "Kept",
              }))
          : [];
        setSpotsDoneRows([...rowsDone, ...kept]);
        setStage("spotsDone");
      } else {
        setStage("saved");
      }
    } catch (err) {
      toast({
        title: "Couldn’t import",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const expanded = tiles.find((t) => t.id === expandedId) || null;

  const resetForRoom = () => {
    tiles.forEach((t) => {
      if (t.localUrl.startsWith("blob:")) URL.revokeObjectURL(t.localUrl);
    });
    blobByTileRef.current.clear();
    stickerBlobByTileRef.current.clear();
    spotBlobsRef.current.clear();
    setTiles([]);
    setSpots([]);
    setSpotExisting({});
    setSpotSavedGroup({});
    setSavedSpotId(null);
    setSpotsMode(false);
    setExistingFor(null);
    setCurrentSpotId(null);
    setSpotDraft(null);
    setSpotShot(null);
    setSpotHint(null);
    setExpandedId(null);
    setAimingId(null);
    setRetakeId(null);
    setSavedCounts(null);
  };

  const startRoomWalk = (name: string) => {
    resetForRoom();
    setCurrentRoom(name);
    // A room that already has items starts with a card: add spots, or add more items.
    setStage(roomItemCount(name) > 0 ? "start" : "live");
  };

  // Opened straight into a room (from the Rooms tab) that already has items: show the same card once.
  const introShown = useRef(false);
  useEffect(() => {
    if (introShown.current || !startRoom) return;
    if (existingUnit.length + existingInv.length + existingAppliances.length === 0) return;
    introShown.current = true;
    if (stage === "live" && tiles.length === 0 && roomItemCount(startRoom) > 0) setStage("start");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingUnit.length, existingInv.length, existingAppliances.length]);

  /** How many things are already captured in each room (units, supplies, appliances). */
  const roomItemCount = (name: string) => {
    const r = normalizeRoomName(name);
    const invRoom = existingInvRooms.find((ir) => normalizeRoomName(ir.name) === r);
    const units = existingUnit.filter((it) => normalizeRoomName(it.room || "") === r).length;
    const appl = existingAppliances.filter((it) => normalizeRoomName(it.location || "") === r).length;
    const supplies = invRoom
      ? existingInv.filter((it) => (it as { room_id?: string | null }).room_id === invRoom.id).length
      : 0;
    return units + appl + supplies;
  };
  const nextRoomName = (() => {
    const from = roomNames.findIndex((n) => normalizeRoomName(n) === normalizeRoomName(currentRoom));
    const order = [...roomNames.slice(from + 1), ...roomNames.slice(0, Math.max(0, from))];
    return order.find((n) => roomItemCount(n) === 0) ?? null;
  })();

  if (desktopHint) {
    return (
      <div className="fixed inset-0 z-[80] bg-[#f4f7f9]" data-ptr-ignore>
        <CapturePhoneHint propertyId={propertyId} onClose={onClose} />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] bg-[#1a2228]" data-ptr-ignore>
      {stage === "pick" && (
        <div className="flex h-full flex-col bg-[#f4f7f9] px-5 pb-6 pt-6">
          <h2 className="text-[30px] font-bold tracking-[-0.6px] text-[#0f1720]">Photograph the unit</h2>
          <div className="mt-1 text-[14.5px] font-medium text-[#64748b]">
            {roomNames.filter((n) => roomItemCount(n) > 0).length} of {roomNames.length} rooms done
          </div>
          <div className="mt-5 flex flex-1 flex-col gap-2 overflow-y-auto">
            {roomNames.map((name) => {
              const n = roomItemCount(name);
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => startRoomWalk(name)}
                  className="flex min-h-[60px] items-center gap-3 rounded-[18px] bg-white px-4 text-left"
                >
                  <span
                    className={cn(
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[14px] font-bold",
                      n > 0 ? "bg-[#15776d] text-white" : "bg-[#e6ecf0] text-[#8494a2]",
                    )}
                  >
                    {n > 0 ? "✓" : ""}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[16.5px] font-bold text-[#0f1720]">{name}</span>
                    <span className="block text-[13px] font-medium text-[#8494a2]">
                      {n > 0 ? `${n} item${n === 1 ? "" : "s"}` : "Not started"}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          {(() => {
            const next = roomNames.find((n) => roomItemCount(n) === 0);
            return next ? (
              <button
                type="button"
                onClick={() => startRoomWalk(next)}
                className="mt-3 h-[58px] rounded-[16px] bg-[#15776d] text-[17px] font-bold text-white shadow-[0_8px_20px_rgba(21,119,109,0.26)]"
              >
                Start {next}
              </button>
            ) : null;
          })()}
          <button type="button" className="mt-1 h-12 text-[15px] font-bold text-[#64748b]" onClick={onClose}>
            Close
          </button>
        </div>
      )}

      {stage === "start" && (
        <div className="flex h-full flex-col bg-[#f4f7f9]">
          <div className="flex items-center gap-3 px-4 pt-5">
            <button
              type="button"
              onClick={() => setStage("pick")}
              aria-label="Back"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[18px] font-bold text-[#0f1720]"
            >
              ‹
            </button>
            <div className="text-[15px] font-bold text-[#475569]">{currentRoom}</div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3 pt-4">
            <div className="text-[34px] font-bold tracking-[-0.8px] text-[#0f1720]">{currentRoom}</div>
            {(() => {
              const items = roomSavedItems();
              const rr = propertyRooms.find((r) => sameRoom(r.name, currentRoom));
              const groupsHere = rr ? existingGroups.filter((g) => g.room_id === rr.id) : [];
              return (
                <div className="mt-3 rounded-[22px] bg-white p-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#e2eeed] text-[#15776d]">✓</span>
                    <div className="text-[19px] font-bold leading-tight text-[#0f1720]">
                      Already saved: {items.length} items, {groupsHere.length} spot{groupsHere.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="mt-3 flex gap-1.5">
                    {items.slice(0, 5).map((it) => (
                      <span key={it.key} className="h-11 w-11 shrink-0 overflow-hidden rounded-[10px] bg-[#e6edf1]">
                        {it.photoUrl ? <img src={it.photoUrl} alt="" className="h-full w-full object-cover" /> : null}
                      </span>
                    ))}
                    {items.length > 5 ? (
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-[#e2eeed] text-[13px] font-bold text-[#15776d]">
                        +{items.length - 5}
                      </span>
                    ) : null}
                  </div>
                  {groupsHere.length > 0 ? (
                    <div className="mt-3 flex flex-col gap-1.5">
                      {groupsHere.map((g) => (
                        <div key={g.id} className="flex items-center gap-2.5 rounded-[12px] bg-[#f4f7f9] p-2">
                          {g.reference_photo_url ? (
                            <img src={g.reference_photo_url} alt="" className="h-9 w-9 shrink-0 rounded-[8px] object-cover" />
                          ) : (
                            <span className="h-9 w-9 shrink-0 rounded-[8px] border-2 border-dashed border-[#cfdbe2]" />
                          )}
                          <span className="min-w-0 flex-1 truncate text-[15px] font-bold text-[#0f1720]">{g.name}</span>
                          {!g.reference_photo_url ? (
                            <span className="rounded-full bg-[#fdf3e5] px-2.5 py-1 text-[11.5px] font-bold text-[#9a5a00]">No photo yet</span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <div className="mt-3 flex items-center gap-2 text-[13.5px] font-medium text-[#64748b]">
                    <span className="text-[#15776d]">✓</span> Item photos stay. Nothing to retake.
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="bg-white px-4 pb-5 pt-3">
            <button
              type="button"
              onClick={() => {
                setSpotsMode(true);
                setStage("live");
                setSpotDraft({ name: "" });
              }}
              className="flex h-14 w-full items-center justify-center rounded-[15px] bg-[#15776d] text-[17px] font-bold text-white"
            >
              Add spots
            </button>
            <button
              type="button"
              onClick={() => {
                setSpotsMode(false);
                setStage("live");
              }}
              className="mt-2 flex h-14 w-full items-center justify-center rounded-[15px] bg-[#e2eeed] text-[16.5px] font-bold text-[#15776d]"
            >
              Add more items
            </button>
          </div>
        </div>
      )}

      {stage === "spotsDone" && (
        <SpotsDoneScreen roomName={currentRoom} photosTaken={spots.length} rows={spotsDoneRows} onSee={() => setStage("summary")} />
      )}

      {stage === "summary" && (() => {
        const rr = propertyRooms.find((r) => sameRoom(r.name, currentRoom));
        const groupsHere = rr ? existingGroups.filter((g) => g.room_id === rr.id) : [];
        const items = roomSavedItems();
        return (
          <RoomSummaryScreen
            roomName={currentRoom}
            total={items.length}
            spots={groupsHere.map((g) => ({
              key: g.id,
              name: g.name,
              photoUrl: g.reference_photo_url,
              itemNames: items.filter((i) => i.groupId === g.id).map((i) => i.name),
            }))}
            openNames={items.filter((i) => !i.groupId || !groupsHere.some((g) => g.id === i.groupId)).map((i) => i.name)}
            onDone={onClose}
          />
        );
      })()}

      {stage === "live" && (
        <div className="relative flex h-full flex-col">
          <video
            ref={videoLiveRef}
            className="h-full w-full object-cover"
            playsInline
            muted
            autoPlay
            onClick={() => {
              if (expandedId) setExpandedId(null);
            }}
          />
          <div className="pointer-events-none absolute inset-x-[18px] top-[22px] flex items-center justify-between">
            <div className="flex h-[34px] items-center rounded-[17px] bg-[rgba(15,23,32,0.55)] px-3.5 text-[13px] font-bold text-white">
              {currentRoom}
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={requestClose}
              className="pointer-events-auto flex h-[34px] w-[34px] items-center justify-center rounded-[17px] bg-[rgba(15,23,32,0.55)] text-white"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6">
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          </div>

          {aimingId && !expanded ? (
            <>
              {(() => {
                const aimingTile = tiles.find((t) => t.id === aimingId);
                if (!aimingTile) return null;
                return (
                  <div className="pointer-events-none absolute inset-x-[18px] top-4 z-10">
                    <div className="flex items-center gap-2.5 rounded-[14px] bg-[rgba(15,23,32,0.72)] px-3.5 py-3">
                      <div className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-[#24a89a]">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[15px] font-bold tracking-[-0.2px] text-white">
                          {aimingTile.name || "Appliance"}
                        </div>
                        <div className="text-[12.5px] font-medium text-[#9fb0bc]">
                          {currentRoom} · named
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="absolute inset-x-0 bottom-0 z-10 rounded-t-[22px] border-t border-[#e6edf1] bg-white px-[18px] pb-6 pt-[18px]">
                <div className="flex flex-col gap-1.5">
                  <div className="text-[18px] font-bold tracking-[-0.3px] text-[#0f1720]">Show the sticker</div>
                  <div className="text-[13.5px] font-medium leading-snug text-[#64748b]">
                    Next shot: the rating label. We read Model and Serial off it.
                  </div>
                </div>
                <div className="mt-3.5 flex items-center gap-3.5">
                  <button
                    type="button"
                    onClick={() => setAimingId(null)}
                    className="flex h-[52px] flex-1 items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] text-[15px] font-bold text-[#64748b]"
                  >
                    Skip
                  </button>
                  <button
                    type="button"
                    aria-label="Capture sticker"
                    onClick={() => void shutter()}
                    disabled={busyShutter}
                    className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full border-[5px] border-[#d8eeeb] bg-[#24a89a] disabled:opacity-60"
                  >
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 8h3l1.6-2.2h6.8L17 8h3v11H4z" />
                      <circle cx="12" cy="13.5" r="3.4" />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          ) : null}

          {!expanded && !aimingId && !spotDraft && !spotPicker && (
            <div className="absolute inset-x-[18px] bottom-[26px] flex flex-col gap-4">
              {spotHint ? (
                <div className="self-center rounded-[17px] bg-[rgba(15,23,32,0.6)] px-4 py-2 text-[13.5px] font-medium text-white">
                  {spotHint}
                </div>
              ) : null}
              {tiles.length === 0 ? (
                <>
                  {showHint && !spotHint ? (
                    <div className="self-center rounded-[17px] bg-[rgba(15,23,32,0.5)] px-4 py-2 text-[13.5px] font-medium text-white/90">
                      Point. Tap. Keep walking.
                    </div>
                  ) : null}
                  <div className="flex h-24 items-center justify-center rounded-[16px] border-[1.5px] border-dashed border-white/30">
                    <div className="text-[13px] font-medium text-white/50">Nothing captured yet</div>
                  </div>
                </>
              ) : (
                <div ref={stripRef} className="flex gap-2.5 overflow-x-auto pb-1">
                  {tiles.map((tile, i) => {
                    const newest = i === tiles.length - 1;
                    return (
                      <button
                        key={tile.id}
                        type="button"
                        onClick={() => {
                          if (tile.status === "failed") return;
                          if (tile.status === "named") setExpandedId(tile.id);
                        }}
                        className={cn("w-[92px] shrink-0 text-left", !newest && tile.status === "named" && "opacity-70")}
                      >
                        <div
                          className={cn(
                            "relative h-[92px] overflow-hidden rounded-[14px]",
                            newest && tile.status !== "failed" && "border-2 border-[#24a89a]",
                            tile.status === "failed" && "border-2 border-[#d98b84]",
                          )}
                        >
                          <img src={tile.localUrl} alt="" className="h-full w-full object-cover" />
                          {tile.status === "reading" ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-[rgba(15,23,32,0.45)] text-[12px] font-bold text-white">
                              Reading…
                            </div>
                          ) : null}
                          {tile.status === "failed" ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-[rgba(15,23,32,0.5)] px-1.5">
                              <div className="text-center text-[11.5px] font-bold leading-tight text-white">
                                Couldn’t name
                              </div>
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startRetake(tile.id);
                                }}
                                className="flex h-[26px] items-center rounded-[13px] bg-white px-2.5 text-[12px] font-bold text-[#0f1720]"
                              >
                                Retake
                              </span>
                            </div>
                          ) : null}
                        </div>
                        <div className="mt-1.5 line-clamp-2 h-8 text-[12.5px] font-bold leading-tight text-white">
                          {tile.status === "reading"
                            ? "Just now"
                            : tile.status === "failed"
                              ? "Couldn’t name"
                              : tile.name}
                        </div>
                        {tile.status === "named" ? (
                          <div className="text-[11.5px] font-medium text-white/60">{kindMeta(tile)}</div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
              <ModeToggle
                inside={!!currentSpot || !!spotDraft}
                onChange={(inside) => {
                  if (inside) {
                    if (currentSpot) return;
                    // Spots already made this walk can be reopened to add more; otherwise start a new one.
                    if (spots.length > 0) setSpotPicker(true);
                    else setSpotDraft({ name: "" });
                  } else {
                    setCurrentSpotId(null);
                    setSpotHint(null);
                  }
                }}
              />
              {currentSpot ? (
                <SpotOpenBar
                  spot={currentSpot}
                  count={namedCount(currentSpotId)}
                  toRetake={toRetake(currentSpotId)}
                  onDone={() => {
                    setCurrentSpotId(null);
                    setSpotHint(null);
                  }}
                />
              ) : null}
              <div className="flex items-center gap-3.5">
                <div className="w-[84px]" />
                <div className="flex flex-1 justify-center">
                  <button
                    type="button"
                    aria-label="Capture"
                    onClick={() => {
                      const r = retakeId;
                      setRetakeId(null);
                      void shutter(r ?? undefined);
                    }}
                    className={cn(
                      "flex h-[84px] w-[84px] items-center justify-center rounded-full border-4",
                      currentSpot ? "border-[#24a89a]" : "border-white/75",
                    )}
                  >
                    <div className="h-16 w-16 rounded-full bg-white" />
                  </button>
                </div>
                {tiles.length > 0 || spots.length > 0 ? (
                  <button
                    type="button"
                    onClick={goConfirm}
                    className="flex h-[50px] w-[84px] items-center justify-center rounded-[14px] bg-[#24a89a] text-[16px] font-bold text-white"
                  >
                    Done
                  </button>
                ) : (
                  <div className="w-[84px]" />
                )}
              </div>
            </div>
          )}

          {expanded ? (
            <div
              className="contents"
              onPointerDown={(e) => {
                const t = e.target;
                if (t instanceof HTMLElement && t.closest("input, textarea")) setViewport(VIEWPORT_LOCKED);
              }}
              onBlur={(e) => {
                const next = e.relatedTarget;
                if (next instanceof Node && e.currentTarget.contains(next)) return;
                setViewport(VIEWPORT);
              }}
            >
              <ReviewSheet
                tile={expanded}
                room={currentRoom}
                spotName={spots.find((x) => x.id === expanded.spotId)?.name ?? null}
                otherSpots={spots.filter((x) => x.id !== expanded.spotId).map((x) => ({ id: x.id, name: x.name }))}
                plateEditing={plateEditing}
                onPlateEditing={setPlateEditing}
                patch={(p) => patchTile(expanded.id, p)}
                onRetake={() => startRetake(expanded.id)}
                onRemove={() => {
                  setTiles((prev) => prev.filter((t) => t.id !== expanded.id));
                  setExpandedId(null);
                }}
                onSave={() => {
                  if (expanded.kind === "inventory" && expanded.track === "fullness" && expanded.level == null) {
                    patchTile(expanded.id, { level: 1 });
                  }
                  setPlateEditing(false);
                  setExpandedId(null);
                }}
                onScanPlate={() => {
                  setAimingId(expanded.id);
                  setExpandedId(null);
                }}
              />
            </div>
          ) : null}

          {existingFor ? (
            <ExistingItemsSheet
              spotName={existingFor.name}
              roomName={currentRoom}
              items={existingCandidates(existingFor.groupId, existingFor.spotId)}
              initialPicked={
                spotExisting[existingFor.spotId] ??
                (existingFor.groupId ? roomSavedItems().filter((i) => i.groupId === existingFor.groupId).map((i) => i.key) : [])
              }
              onUse={(keys) => {
                setSpotExisting((prev) => ({ ...prev, [existingFor.spotId]: keys }));
                setSpotHint(
                  keys.length > 0
                    ? `${existingFor.name}: ${keys.length} item${keys.length === 1 ? "" : "s"} added. Tap more or press Done.`
                    : `${existingFor.name} is set. Tap what’s inside.`,
                );
                const sid = existingFor.spotId;
                setExistingFor(null);
                if (spotsMode) setSavedSpotId(sid);
              }}
              onSkip={() => {
                const sid = existingFor.spotId;
                setExistingFor(null);
                if (spotsMode) setSavedSpotId(sid);
              }}
            />
          ) : null}
          {savedSpotId ? (
            (() => {
              const sp = spots.find((x) => x.id === savedSpotId);
              if (!sp) return null;
              const picks = spotExisting[sp.id] ?? [];
              const byKey = new Map(roomSavedItems().map((i) => [i.key, i.name]));
              const claimedAll = new Set(Object.values(spotExisting).flat());
              const stillOpen = roomSavedItems().filter((i) => !claimedAll.has(i.key) && !i.groupId).length;
              return (
                <SpotSavedScreen
                  localUrl={sp.localUrl}
                  name={sp.name}
                  roomName={currentRoom}
                  itemNames={picks.map((k) => byKey.get(k) ?? "").filter(Boolean)}
                  stillOpen={stillOpen}
                  onAnother={() => {
                    setSavedSpotId(null);
                    setSpotDraft({ name: "" });
                  }}
                  onDone={() => {
                    setSavedSpotId(null);
                    void handleImport();
                  }}
                />
              );
            })()
          ) : null}
          {spotPicker ? (
            <SpotPickSheet
              spots={spots}
              countOf={namedCount}
              onPick={(id) => {
                setCurrentSpotId(id);
                setSpotHint(null);
                setSpotPicker(false);
              }}
              onNew={() => {
                setSpotPicker(false);
                setSpotDraft({ name: "" });
              }}
              onCancel={() => setSpotPicker(false)}
            />
          ) : null}
          {spotDraft && !spotShot ? (
            <SpotShoot
              name={spotDraft.name || null}
              busy={busyShutter}
              onShoot={() => void shootSpot()}
              onBack={() => setSpotDraft(null)}
            />
          ) : null}
          {spotDraft && spotShot && spotDraft.replaceId ? (
            <SpotReview
              name={spotDraft.name}
              localUrl={spotShot.localUrl}
              onUse={() => useSpotPhoto()}
              onRetake={() => {
                URL.revokeObjectURL(spotShot.localUrl);
                setSpotShot(null);
              }}
            />
          ) : null}
          {spotDraft && spotShot && !spotDraft.replaceId ? (
            <SpotNameSheet
              localUrl={spotShot.localUrl}
              roomName={currentRoom}
              usedNames={spots.map((x) => x.name)}
              saved={(() => {
                const rr = propertyRooms.find((r) => sameRoom(r.name, currentRoom));
                if (!rr) return [];
                return existingGroups
                  .filter((g) => g.room_id === rr.id)
                  .map((g) => ({
                    name: g.name,
                    photoUrl: g.reference_photo_url,
                    itemCount: roomSavedItems().filter((i) => i.groupId === g.id).length,
                  }));
              })()}
              hasCandidates={existingCandidates().length > 0}
              onUse={(name) => useSpotPhoto(name)}
              onRetake={() => {
                URL.revokeObjectURL(spotShot.localUrl);
                setSpotShot(null);
              }}
            />
          ) : null}

          {leaveOpen ? (
            <div className="absolute inset-0 flex items-center justify-center bg-[rgba(15,23,32,0.45)] px-6">
              <div className="w-full rounded-[20px] bg-white p-[22px]">
                <div className="text-[21px] font-bold tracking-[-0.4px]">Leave without saving?</div>
                <p className="mt-2 text-[14.5px] font-medium leading-snug text-[#64748b]">
                  {tiles.length} capture{tiles.length === 1 ? "" : "s"} in this session haven’t been imported to{" "}
                  {currentRoom} yet.
                </p>
                <div className="mt-3.5 flex gap-2">
                  {tiles.slice(0, 4).map((t) => (
                    <img key={t.id} src={t.localUrl} alt="" className="h-[58px] w-[58px] rounded-[12px] object-cover" />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setLeaveOpen(false)}
                  className="mt-4 h-[54px] w-full rounded-[14px] bg-[#24a89a] text-[16px] font-bold text-white"
                >
                  Keep capturing
                </button>
                <button
                  type="button"
                  onClick={discard}
                  className="mt-2 h-[54px] w-full rounded-[14px] border-[1.5px] border-[#e6edf1] text-[16px] font-bold text-[#b4453c]"
                >
                  Discard {tiles.length} capture{tiles.length === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {stage === "confirm" && (
        <>
          <ConfirmGrouped
            room={currentRoom}
            rows={namedTiles.map((t) => ({
              id: t.id,
              name: t.name,
              localUrl: t.localUrl,
              isUnit: t.kind === "unit",
              spotId: t.spotId,
              kindLabel: t.kind === "unit" ? "Unit item" : t.kind === "appliance" ? "Appliance" : t.kind === "part" ? "Part" : "Supply",
              meta:
                spots.length > 0
                  ? `${kindMeta(t)} · ${t.kind === "unit" ? "Unit item" : t.kind === "appliance" ? "Appliance" : t.kind === "part" ? "Part" : "Supply"}`
                  : kindMeta(t),
            }))}
            spots={spots}
            importing={importing}
            onOpenRow={(id) => {
              setStage("live");
              setExpandedId(id);
            }}
            onMoveRow={(row) => setMoveRow(row)}
            onRetakeSpot={(id) => {
              const sp = spots.find((x) => x.id === id);
              if (!sp) return;
              setSpotDraft({ name: sp.name, replaceId: id });
              setStage("live");
            }}
            onRemoveSpot={removeSpot}
            onAddItems={(id) => {
              setCurrentSpotId(id);
              setSpotHint(null);
              setStage("live");
            }}
            onImport={() => void handleImport()}
            onBack={() => setStage("live")}
          />
          {moveRow ? (
            <div className="absolute inset-0 z-40">
              <MoveSheet
                row={moveRow}
                spots={spots}
                onPick={(spotId) => {
                  patchTile(moveRow.id, { spotId });
                  setMoveRow(null);
                }}
                onCancel={() => setMoveRow(null)}
              />
            </div>
          ) : null}
        </>
      )}

      {stage === "saved" && (
        <div className="flex h-full flex-col items-center justify-center bg-[#f4f7f9] px-6 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#15776d] text-[30px] text-white">✓</div>
          <h2 className="mt-4 text-[28px] font-bold tracking-[-0.5px] text-[#0f1720]">{currentRoom} is saved</h2>
          {savedCounts ? (
            <div className="mt-5 flex gap-3">
              {[
                { n: savedCounts.items, l: "Items" },
                { n: savedCounts.supplies, l: "Supplies" },
                { n: savedCounts.spots, l: "Spots" },
              ].map((c) => (
                <div key={c.l} className="min-w-[88px] rounded-[18px] bg-white px-4 py-3">
                  <div className="text-[28px] font-bold text-[#0f1720]">{c.n}</div>
                  <div className="text-[13px] font-bold text-[#8494a2]">{c.l}</div>
                </div>
              ))}
            </div>
          ) : null}
          {nextRoomName ? (
            <button
              type="button"
              onClick={() => startRoomWalk(nextRoomName)}
              className="mt-8 h-[58px] w-full max-w-[420px] rounded-[16px] bg-[#15776d] text-[17px] font-bold text-white"
            >
              Next room: {nextRoomName}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              resetForRoom();
              setStage("pick");
            }}
            className="mt-2 h-[54px] w-full max-w-[420px] rounded-[16px] bg-white text-[16px] font-bold text-[#0f1720]"
          >
            All rooms
          </button>
          <button type="button" onClick={onClose} className="mt-1 h-12 text-[15px] font-bold text-[#64748b]">
            Done for now
          </button>
        </div>
      )}
    </div>
  );
}
